import React, { CSSProperties, SyntheticEvent } from 'react';
import config from '../config/environment';

/**
 * Convert relative profile image URLs to absolute URLs
 */
export const getFullImageUrl = (imageUrl: string | null | undefined): string | null => {
  if (!imageUrl) {
    return null;
  }

  // If it's already an absolute URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative URL, prepend the API base URL
  if (imageUrl.startsWith('/')) {
    return `${config.API_BASE_URL}${imageUrl}`;
  }

  // If it doesn't start with /, add both / and the API base URL
  return `${config.API_BASE_URL}/${imageUrl}`;
};

interface ProfileImageProps {
  src?: string | null;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  onError?: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
}

/**
 * Profile image component with fallback for broken images
 */
export const ProfileImage: React.FC<ProfileImageProps> = ({
  src,
  alt = "Profile",
  className = "",
  style = {},
  onError = null
}) => {
  const handleError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    // Hide the broken image and show a fallback
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
    if (target.nextSibling instanceof HTMLElement) {
      target.nextSibling.style.display = 'flex';
    }
    if (onError) {
      onError(e);
    }
  };

  const fullImageUrl = getFullImageUrl(src);

  return (
    <div style={{ position: 'relative', ...style }} className={className}>
      {fullImageUrl && (
        <img
          src={fullImageUrl}
          alt={alt}
          onError={handleError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit'
          }}
        />
      )}
      <div
        style={{
          display: fullImageUrl ? 'none' : 'flex',
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-surface-muted)',
          borderRadius: 'inherit'
        }}
      >
        <i className="fas fa-user" style={{ color: '#8a90a8' }}></i>
      </div>
    </div>
  );
};
